// useSite — PowerSync-backed hook for a single site row, hydrated
// with its parent base summary. Replaces the REST `GET /sites/:id`
// call used by SiteCaptureScreen during deep-link load.
//
// Returns the same minimal SiteMetadata shape consumers already
// expect: { id, name }. The base summary is exposed alongside so the
// breadcrumb / hero can render the parent context without a second
// hook.
//
// Stale-data guard: a /capture/sites/A → /capture/sites/B navigation
// keeps useQuery returning A's row until B's row arrives. The id
// freshness check suppresses A's data under B's URL during the
// transition.
//
// Initial-sync gate: a freshly-installed device may not have the
// site row yet; treat an empty result as "still loading" until the
// reference_site_scoped stream has completed at least one sync.

import { useAllSyncStreamsHaveSynced, useQuery } from '@powersync/react'

import { db } from './client'

export interface UseSiteRow {
  id: string
  name: string
  code: string
  base_id: string
  display_order: number
}

export interface UseSiteBase {
  id: string
  name: string
}

export interface UseSiteResult {
  site: UseSiteRow | null
  base: UseSiteBase | null
  isLoading: boolean
  isFetching: boolean
  error: Error | undefined
}

export function useSite(siteId: string): UseSiteResult {
  const siteResult = useQuery<UseSiteRow>(
    `SELECT id, name, code, base_id, display_order
     FROM sites
     WHERE id = ?
     LIMIT 1`,
    [siteId],
  )

  const parentBaseId = siteResult.data[0]?.base_id ?? ''
  const baseResult = useQuery<UseSiteBase>(
    `SELECT id, name FROM bases WHERE id = ? LIMIT 1`,
    [parentBaseId],
  )

  const referenceSiteScopedSynced = useAllSyncStreamsHaveSynced(db, [
    { name: 'reference_site_scoped', waitForStream: true },
  ])

  const siteIsForSiteId =
    siteResult.data.length === 0 ||
    (siteResult.data[0]?.id ?? '') === siteId
  const isStale = !siteIsForSiteId

  const siteIsEmptyButUnsynced =
    siteResult.data.length === 0 && !referenceSiteScopedSynced

  const isLoading =
    siteResult.isLoading ||
    baseResult.isLoading ||
    isStale ||
    siteIsEmptyButUnsynced

  const site = !isStale ? (siteResult.data[0] ?? null) : null
  const base =
    !isStale && baseResult.data.length > 0 ? baseResult.data[0] ?? null : null

  return {
    site,
    base,
    isLoading,
    isFetching: siteResult.isFetching || baseResult.isFetching,
    error: siteResult.error ?? baseResult.error,
  }
}
